import * as React from 'react';
import {
  AddIcon,
  HStack,
  IconButton,
  Spinner,
  Text,
  VStack,
  View,
} from '@packages/systems/spring';
import type {QuestionOption} from '../../../selectors';
import {AskUserQuestionCard} from './AskUserQuestionCard';
import {useClarificationPing} from '../useClarificationPing';
import {emotionStyled} from '@packages/utilities/styled';
// Deep import (not the agent/frontend barrel) so we only pull the chat
// textarea, not AssistantChat and its heavier ESM-only deps.
// eslint-disable-next-line webflow/module-top-level-imports
import {
  Textarea,
  TextareaActions,
  TextareaContainer,
  TextareaInput,
  useTextarea,
} from '@packages/systems/ai/agent/frontend/chat';

export type CodeComponentClarificationViewProps = {
  /**
   * User prompt that triggered this clarification turn. Shown in the
   * PromptBox above the loading spinner / question card. The loading
   * textarea stays empty (message already “sent”).
   */
  userPrompt?: string | null;
  /**
   * Pending structured question. When null/undefined, shows the thinking
   * spinner plus a loading textarea (Stop cancels) while the agent
   * prepares the first ask-user-question call.
   */
  pending?: {
    question: string;
    options: QuestionOption[];
    resetKey?: string;
  } | null;
  onSelect: (option: QuestionOption) => void;
  onSelectFreeForm: (text: string) => void;
  onCancel: () => void;
};

const PromptBox = emotionStyled(Text)({
  width: '75%',
  background: '$backgroundSecondary',
  padding: '$2',
  borderRadius: '$2',
});

const LOADING_TEXTAREA_PLACEHOLDER =
  'What do you want to make? Start typing or attach a reference.';

/**
 * Presentational fullscreen AICC clarification shell (centered 640px column).
 * Flux / ViewportLayer wiring lives in CodeComponentClarification.
 * Shows Q1 only — follow-ups continue in the AIA sidebar after the user answers.
 */
export const CodeComponentClarificationView: React.FC<
  CodeComponentClarificationViewProps
> = ({userPrompt, pending, onSelect, onSelectFreeForm, onCancel}) => {
  useClarificationPing(Boolean(pending));

  const textarea = useTextarea({
    initialValue: '',
    isLoading: true,
    onCancel,
  });

  return (
    <View
      background="$background1"
      width="100%"
      height="100%"
      display="flex"
      justifyContent="center"
      userSelect="none"
      color="actionPrimaryText"
    >
      <VStack
        width={640}
        height="100%"
        align="end"
        position="relative"
        paddingTop="$16"
        paddingBottom="$8"
        spacing={4}
      >
        {userPrompt ? (
          <PromptBox
            font="helpText"
            data-automation-id="code-component-clarification-user-prompt"
          >
            {userPrompt}
          </PromptBox>
        ) : null}
        <View
          width="100%"
          flex={1}
          display="flex"
          flexDirection="column"
          minHeight={0}
        >
          {pending ? (
            <VStack spacing={4}>
              <Text>One question before we dive in →</Text>
              <AskUserQuestionCard
                question={pending.question}
                options={pending.options}
                resetKey={pending.resetKey}
                onSelect={onSelect}
                onSelectFreeForm={onSelectFreeForm}
                onCancel={onCancel}
              />
            </VStack>
          ) : (
            <>
              <HStack data-automation-id="code-component-clarification-loading-status">
                <Spinner size={16} />
                <Text colorVariant="secondary" textAlign="center">
                  Generating clarifying questions…
                </Text>
              </HStack>
              {/* Flex spacer pushes the loading textarea to the bottom. */}
              <View flex={1} minHeight={48} />
              <Textarea
                contextValue={textarea.contextValue}
                minHeight={140}
                data-automation-id="code-component-clarification-loading-textarea"
              >
                <TextareaContainer>
                  <TextareaInput placeholder={LOADING_TEXTAREA_PLACEHOLDER} />
                </TextareaContainer>
                <TextareaActions>
                  <IconButton
                    label="Add image"
                    Icon={AddIcon}
                    variant="ghost"
                    density="comfortable"
                    isDisabled
                    data-automation-id="code-component-clarification-loading-attach"
                  />
                </TextareaActions>
              </Textarea>
            </>
          )}
        </View>
      </VStack>
    </View>
  );
};
